import type { AppearanceRow } from '@setplex/tria-api'
import { match, nil, onTerms, payload, variant, type VariantOf } from 'variant'

export { isType } from 'variant'

export type Row = {
  id: string
  desc: AppearanceRow
  data: RowData
  hasMore?: boolean
}

export type ViewRow = {
  id: string
  content: unknown[]
  isMoreThanOnePage: boolean
  appearance: AppearanceRow
  type: Row['data']['type']
}

export type RowData = VariantOf<typeof RowData>
export const RowData = variant(
  onTerms(({ T }) => ({
    Empty: nil,
    Loading: nil,
    Failed: payload<unknown>(),
    Dirty: payload(T),
    Updating: payload(T),
    Awaiting: payload(T),
    Ready: payload(T),
  }))
)

export const { Empty, Loading, Failed, Dirty, Updating, Awaiting, Ready } =
  RowData

//
// row transition helpers
//

/**
 * Change row to dirty state
 */
export function toDirty(row: Row): Row {
  const { refreshable } = row.desc
  return {
    ...row,
    data: match(row.data, {
      Empty: (data) => data, // leave as is
      Loading: (data) => (refreshable ? Empty() : data), // if refreshable, drop loading state and reset
      Failed: () => Empty(), // reset failed state -> try to reload
      Dirty: (data) => data, // leave as is
      Updating: (data) => (refreshable ? Empty() : data), // if refreshable, drop updating state and reset
      Awaiting: (data) => (refreshable ? Dirty(data.payload) : data), // if refreshable, set dirty
      Ready: (data) => (refreshable ? Dirty(data.payload) : data), // if refreshable, set dirty
    }),
  }
}

/**
 * Change row to loading state
 */
export function toLoading(row: Row): Row {
  return {
    ...row,
    data: match(row.data, {
      Empty: () => Loading(),
      Loading: (data) => data, // leave as is
      Failed: () => Loading(),
      Dirty: (data) => Updating(data.payload),
      Updating: (data) => data, // leave as is
      Awaiting: (data) => Updating(data.payload), // do not erase!
      Ready: (data) => Updating(data.payload), // do not erase!
    }),
  }
}
